/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { getAxiosForClient } from '@/lib/axios';
import {
  type Mutation,
  type MutationState,
  type QueryClient,
  useMutation as useMutationHook,
  useMutationState as useMutationStateHook,
} from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import { useCallback } from 'react';

type CustomUseMutationOptions<Data, Options, Error> = {
  onSuccess?: (data: Data, variables: Options) => void;
  onError?: (error: Error, variables: Options) => void;
};

export type CustomUseMutationStateOptions<Data, SelectData = Data> = {
  select?: (data: Data) => SelectData;
};

type CustomOptions = {
  ensureOnlyOneMutation?: boolean;
};

// extracted from React Query
const defaultState = {
  context: undefined,
  data: undefined,
  error: null,
  failureCount: 0,
  failureReason: null,
  isPaused: false,
  status: 'idle',
  variables: undefined,
  submittedAt: 0,
} as const;

export function createMutationState<Data, Options>(
  primaryKey: string[],
  get: (options: Options, axiosInstance: AxiosInstance) => Promise<Data>,
  options: CustomOptions = {},
) {
  const mutationKey = primaryKey;

  const mutationFn = async (options: Options) => {
    return get(options, getAxiosForClient());
  };

  function useMutation({ onSuccess, onError }: CustomUseMutationOptions<Data, Options, unknown>) {
    const { mutate, mutateAsync, submittedAt, variables, reset } = useMutationHook({
      mutationKey,
      mutationFn,
      onSuccess,
      onError,
    });
    return { mutate, mutateAsync, submittedAt, variables, reset };
  }

  function useMutationState<SelectData = Data>({
    select: selectFn,
  }: CustomUseMutationStateOptions<Data, SelectData> = {}) {
    const select = useCallback(
      (mutation: Mutation): MutationState<SelectData, Error, unknown, unknown> | undefined => {
        return selectFn
          ? {
              ...mutation.state,
              data: selectFn(mutation.state.data as Data),
            }
          : (mutation.state as MutationState<SelectData>);
      },
      [selectFn],
    );

    const results = useMutationStateHook({
      filters: {
        mutationKey,
        exact: true,
      },
      select,
    });

    return results[results.length - 1] ?? defaultState;
  }

  function setData(queryClient: QueryClient, data: Data) {
    if (options.ensureOnlyOneMutation) {
      clearMutations(queryClient);
    }

    const cache = queryClient.getMutationCache();

    const newData = cache.build(
      queryClient,
      { mutationFn, mutationKey },
      {
        ...defaultState,
        status: 'success',
        data: data,
      },
    );

    cache.add(newData);
  }

  function clearMutations(queryClient: QueryClient) {
    const cache = queryClient.getMutationCache();

    const mutations = cache.findAll({
      mutationKey,
      exact: true,
    });

    mutations.forEach((mutation) => {
      cache.remove(mutation);
    });
  }

  return { useMutation, useMutationState, setData, clearMutations } as const;
}
